import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Fab from '@mui/material/Fab';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Divider from '@mui/material/Divider';
import Snackbar from '@mui/material/Snackbar';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import HomeIcon from '@mui/icons-material/Home';
import BookIcon from '@mui/icons-material/Book';
import QuizIcon from '@mui/icons-material/Quiz';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import SpeedIcon from '@mui/icons-material/Speed';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import HighlightIcon from '@mui/icons-material/Highlight';
import NotesIcon from '@mui/icons-material/Notes';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import Layout from './Layout';
import TableOfContents from './TableOfContents';
import StudyModeSelector from './StudyModeSelector';
import Flashcards from './Flashcards';
import StudyNotes from './StudyNotes';
import SummaryView from './SummaryView';
import Quiz from './Quiz';
import { modulesList } from '../data/modules';
import { useProgress, saveModuleProgress } from '../hooks/useProgress';
import { useGameStats } from '../hooks/useGameStats';

// Styled components for modern content display
const ContentContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(4),
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: '1fr 300px',
  },
}));

const StudyToolbar = styled(Paper)(({ theme }) => ({
  background: '#1a1a2e',
  borderRadius: '16px',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  border: '1px solid rgba(102, 126, 234, 0.1)',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  position: 'sticky',
  top: theme.spacing(8),
  zIndex: 10,
}));

const StudyContent = styled(Paper)(({ theme }) => ({
  background: '#1a1a2e',
  color: '#e2e8f0',
  borderRadius: '24px',
  padding: theme.spacing(6),
  border: '1px solid rgba(102, 126, 234, 0.1)',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
  lineHeight: 1.9,
  fontSize: '1.125rem',
  transition: 'all 0.3s ease',
  
  // Enhanced markdown styling
  '& h1, & h2, & h3, & h4': {
    color: '#f3f4f6',
  },
  '& h1': {
    fontSize: 'clamp(2rem, 4vw, 2.75rem)',
    fontWeight: 800,
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(6),
    paddingBottom: theme.spacing(2),
    borderBottom: '4px solid #667eea',
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  '& h2': {
    fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
    fontWeight: 700,
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(5),
    paddingLeft: theme.spacing(2.5),
    borderLeft: '5px solid #667eea',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '5px',
      background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
    },
  },
  '& h3': {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#d1d5db',
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(3),
  },
  '& h4': {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#d1d5db',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  '& p': {
    marginBottom: theme.spacing(2.5),
    color: '#e2e8f0',
    textAlign: 'justify',
    fontSize: 'clamp(1rem, 1.5vw, 1.125rem)',
    lineHeight: 1.8,
  },
  '& ul, & ol': {
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    '& li': {
      marginBottom: theme.spacing(0.5),
      color: '#d1d5db',
      '& strong': {
        color: '#f3f4f6',
        fontWeight: 600,
      },
    },
  },
  '& blockquote': {
    borderLeft: '4px solid #e2e8f0',
    paddingLeft: theme.spacing(2),
    margin: theme.spacing(2, 0),
    color: '#9ca3af',
    fontStyle: 'italic',
    background: '#374151',
    padding: theme.spacing(2),
    borderRadius: '8px',
  },
  '& strong': {
    color: '#f3f4f6',
    fontWeight: 600,
  },
  '& em': {
    color: '#667eea',
    fontStyle: 'italic',
  },
  '& code': {
    background: '#374151',
    padding: '0.2rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontFamily: 'Monaco, Consolas, monospace',
    color: '#f59e0b',
  },
  '& pre': {
    background: '#111827',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: theme.spacing(2),
    overflow: 'auto',
    margin: theme.spacing(2, 0),
  },
  '& .highlighted': {
    backgroundColor: 'rgba(102, 126, 234, 0.2)',
    padding: '2px 0',
    borderRadius: '3px',
  },
}));

const NavigationSidebar = styled(Paper)(({ theme }) => ({
  background: '#1a1a2e',
  borderRadius: '20px',
  padding: theme.spacing(4),
  border: '1px solid rgba(102, 126, 234, 0.1)',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
  height: 'fit-content',
  position: 'sticky',
  top: theme.spacing(12),
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  boxShadow: variant === 'contained' ? '0 4px 16px rgba(102, 126, 234, 0.3)' : 'none',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: variant === 'contained' ? '0 8px 25px rgba(102, 126, 234, 0.4)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
}));

const FloatingActionButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    transform: 'scale(1.1)',
  },
}));

const BreadcrumbContainer = styled(Box)(({ theme }) => ({
  background: '#1a1a2e',
  padding: theme.spacing(2.5, 4),
  borderRadius: '16px',
  marginBottom: theme.spacing(4),
  border: '1px solid rgba(102, 126, 234, 0.1)',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
}));

const ProgressCard = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea30 0%, #764ba230 100%)',
  borderRadius: '16px',
  padding: theme.spacing(3),
  border: '1px solid #667eea40',
  marginBottom: theme.spacing(3),
}));

const SectionCheckbox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: 'pointer',
  padding: theme.spacing(1),
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(102, 126, 234, 0.1)',
  },
}));

export default function EnhancedModulePage() {
  const { moduleId } = useParams();
  const [content, setContent] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  // Reading mode removed - using dark theme only
  const [fontSize, setFontSize] = useState('medium');
  const [isReading, setIsReading] = useState(false);
  const [readingSpeed, setReadingSpeed] = useState(1);
  const [bookmarked, setBookmarked] = useState(false);
  const [completedSections, setCompletedSections] = useState([]);
  const [notes, setNotes] = useState({});
  const [studyMode, setStudyMode] = useState('reading');
  const [questions, setQuestions] = useState([]);
  const [achievementSnackbar, setAchievementSnackbar] = useState({ open: false, message: '' });
  const [studyStartTime, setStudyStartTime] = useState(Date.now());
  const contentRef = useRef(null);
  const speechRef = useRef(null);

  const module = modulesList.find(m => m.id === moduleId);
  const progress = useProgress();
  const moduleProgress = progress[moduleId] || { completed: 0, sections: [] };
  const gameStats = useGameStats();

  useEffect(() => {
    setLoading(true);
    fetch(`/resumos/${moduleId}.md`)
      .then((res) => {
        if (!res.ok) throw new Error('not found');
        return res.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [moduleId]);

  useEffect(() => {
    // Load saved progress
    const savedProgress = progress[moduleId];
    if (savedProgress) {
      setCompletedSections(savedProgress.sections || []);
      setBookmarked(savedProgress.bookmarked || false);
      setNotes(savedProgress.notes || {});
    }
  }, [moduleId, progress]);

  useEffect(() => {
    // Load quiz questions for practice mode
    if (studyMode === 'practice') {
      fetch(`/quizzes/${moduleId}.json`)
        .then((res) => res.json())
        .then((data) => setQuestions(data.questions || []))
        .catch(() => setQuestions([]));
    }
  }, [moduleId, studyMode]);

  useEffect(() => {
    // Track study time
    const interval = setInterval(() => {
      const minutesStudied = Math.floor((Date.now() - studyStartTime) / 60000);
      if (minutesStudied >= 10) {
        gameStats.addStudyTime(10);
        setStudyStartTime(Date.now());
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [studyStartTime, gameStats]);

  useEffect(() => {
    // Check daily streak
    gameStats.checkDailyStreak();
  }, [gameStats]);

  // Reading mode handler removed - using dark theme only

  const handleFontSizeChange = (event, newSize) => {
    if (newSize !== null) {
      setFontSize(newSize);
      // Apply font size to content
      if (contentRef.current) {
        const sizes = { small: '1rem', medium: '1.125rem', large: '1.3rem' };
        contentRef.current.style.fontSize = sizes[newSize];
      }
    }
  };

  const toggleTextToSpeech = () => {
    if (!isReading) {
      const text = contentRef.current?.innerText || '';
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = readingSpeed;
      speechRef.current = utterance;
      window.speechSynthesis.speak(utterance);
      setIsReading(true);
      
      utterance.onend = () => {
        setIsReading(false);
      };
    } else {
      window.speechSynthesis.cancel();
      setIsReading(false);
    }
  };

  const handleSpeedChange = (event, newSpeed) => {
    if (newSpeed !== null) {
      setReadingSpeed(newSpeed);
      if (isReading) {
        window.speechSynthesis.cancel();
        toggleTextToSpeech();
      }
    }
  };

  const toggleBookmark = () => {
    setBookmarked(!bookmarked);
    saveModuleProgress(moduleId, {
      ...moduleProgress,
      bookmarked: !bookmarked,
    });
  };

  const toggleSectionComplete = (sectionId) => {
    const newCompletedSections = completedSections.includes(sectionId)
      ? completedSections.filter(id => id !== sectionId)
      : [...completedSections, sectionId];
    
    setCompletedSections(newCompletedSections);
    
    // Calculate progress percentage
    const totalSections = content.match(/^#{1,3}\s.+$/gm)?.length || 1;
    const completedPercentage = Math.round((newCompletedSections.length / totalSections) * 100);
    
    saveModuleProgress(moduleId, {
      ...moduleProgress,
      sections: newCompletedSections,
      completed: completedPercentage,
    });

    // Check if module is completed
    if (completedPercentage === 100 && !moduleProgress.completed) {
      gameStats.incrementModulesCompleted();
      setAchievementSnackbar({
        open: true,
        message: '🎉 Módulo completo! +50 pontos',
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <Layout>
        <BreadcrumbContainer>
          <Skeleton variant="text" width={200} height={30} />
        </BreadcrumbContainer>
        
        <ContentContainer>
          <StudyContent elevation={0}>
            <Skeleton variant="text" width="80%" height={60} sx={{ mb: 3 }} />
            <Skeleton variant="text" width="100%" height={30} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="100%" height={30} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="90%" height={30} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="95%" height={30} sx={{ mb: 4 }} />
            
            <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="100%" height={30} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="100%" height={30} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="85%" height={30} />
          </StudyContent>
        </ContentContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumb Navigation */}
      <BreadcrumbContainer>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            component={RouterLink}
            to="/"
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              color: '#cbd5e1',
              textDecoration: 'none',
              '&:hover': { color: '#667eea' },
            }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
            Início
          </Link>
          <Typography 
            color="text.primary" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              fontWeight: 600,
            }}
          >
            <BookIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
            {module?.title || moduleId}
          </Typography>
        </Breadcrumbs>
      </BreadcrumbContainer>

      {/* Study Toolbar */}
      <StudyToolbar elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          {/* Font Size */}
          <ToggleButtonGroup
            value={fontSize}
            exclusive
            onChange={handleFontSizeChange}
            size="small"
          >
            <ToggleButton value="small" aria-label="small font">
              <Tooltip title="Fonte Pequena">
                <Box sx={{ fontSize: '0.875rem' }}>A</Box>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="medium" aria-label="medium font">
              <Tooltip title="Fonte Média">
                <Box sx={{ fontSize: '1rem' }}>A</Box>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="large" aria-label="large font">
              <Tooltip title="Fonte Grande">
                <Box sx={{ fontSize: '1.25rem' }}>A</Box>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem />

          {/* Text to Speech */}
          <Tooltip title={isReading ? "Pausar Leitura" : "Iniciar Leitura"}>
            <IconButton onClick={toggleTextToSpeech} color="primary">
              {isReading ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Velocidade de Leitura">
            <ToggleButtonGroup
              value={readingSpeed}
              exclusive
              onChange={handleSpeedChange}
              size="small"
              disabled={!isReading}
            >
              <ToggleButton value={0.75} aria-label="slow">
                0.75x
              </ToggleButton>
              <ToggleButton value={1} aria-label="normal">
                1x
              </ToggleButton>
              <ToggleButton value={1.25} aria-label="fast">
                1.25x
              </ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>

          <Divider orientation="vertical" flexItem />

          {/* Bookmark */}
          <Tooltip title={bookmarked ? "Remover dos Favoritos" : "Adicionar aos Favoritos"}>
            <IconButton onClick={toggleBookmark} color="primary">
              {bookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
            </IconButton>
          </Tooltip>

          {/* Notes */}
          <Tooltip title="Adicionar Nota">
            <IconButton color="primary">
              <NotesIcon />
            </IconButton>
          </Tooltip>

          {/* Highlight */}
          <Tooltip title="Destacar Texto">
            <IconButton color="primary">
              <HighlightIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Progress Indicator */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {gameStats.stats.currentStreak > 0 && (
            <Chip
              icon={<LocalFireDepartmentIcon />}
              label={`${gameStats.stats.currentStreak} dias`}
              color="error"
              variant="filled"
              size="small"
            />
          )}
          <Chip
            label={`${moduleProgress.completed || 0}% Concluído`}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>
      </StudyToolbar>

      {error ? (
        <Alert 
          severity="error" 
          sx={{ 
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
          }}
        >
          Resumo não encontrado. Verifique se o conteúdo está disponível.
        </Alert>
      ) : (
        <>
          {/* Study Mode Selector */}
          <StudyModeSelector mode={studyMode} onModeChange={setStudyMode} />
          
          <ContentContainer>
            {/* Main Content */}
            <Box>
              {studyMode === 'reading' && (
                <StudyContent elevation={0} ref={contentRef}>
                  <ReactMarkdown>{content}</ReactMarkdown>
                  
                  {/* Action Buttons */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    mt: 6, 
                    pt: 4, 
                    borderTop: '1px solid #e2e8f0',
                    flexWrap: 'wrap',
                  }}>
                    <ActionButton
                      component={RouterLink}
                      to="/"
                      variant="outlined"
                      startIcon={<HomeIcon />}
                    >
                      Voltar ao Início
                    </ActionButton>
                    <ActionButton
                      component={RouterLink}
                      to={`/modulos/${moduleId}/quiz`}
                      variant="contained"
                      startIcon={<QuizIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                        },
                      }}
                    >
                      Fazer Quiz
                    </ActionButton>
                  </Box>
                </StudyContent>
              )}
              
              {studyMode === 'flashcards' && (
                <StudyContent elevation={0}>
                  <Flashcards content={content} moduleId={moduleId} />
                </StudyContent>
              )}
              
              {studyMode === 'practice' && (
                <StudyContent elevation={0}>
                  {questions.length > 0 ? (
                    <Quiz questions={questions} onFinish={(score) => {
                      const percentage = (score / questions.length) * 100;
                      saveModuleProgress(moduleId, {
                        ...moduleProgress,
                        lastPracticeScore: percentage,
                      });
                      gameStats.recordQuizScore(score, questions.length);
                      setAchievementSnackbar({
                        open: true,
                        message: `🎯 Quiz concluído! ${Math.round(percentage)}% de acertos! +${Math.round(percentage * 2)} pontos`,
                      });
                    }} />
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="text.secondary">
                        Carregando questões de prática...
                      </Typography>
                    </Box>
                  )}
                </StudyContent>
              )}
              
              {studyMode === 'summary' && (
                <SummaryView content={content} />
              )}
            </Box>

          {/* Navigation Sidebar */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Progress Card */}
            <ProgressCard elevation={0}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#f1f5f9', mb: 2 }}>
                Seu Progresso
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Conclusão do Módulo
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="primary">
                    {moduleProgress.completed || 0}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={moduleProgress.completed || 0}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'rgba(102, 126, 234, 0.2)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Marque as seções como concluídas para acompanhar seu progresso
              </Typography>
            </ProgressCard>

            {/* Table of Contents with Section Tracking */}
            <NavigationSidebar elevation={0}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#f1f5f9' }}>
                Índice
              </Typography>
              <TableOfContents 
                content={content} 
                containerRef={contentRef}
                completedSections={completedSections}
                onToggleSection={toggleSectionComplete}
              />
            </NavigationSidebar>

            {/* Quick Actions */}
            <NavigationSidebar elevation={0}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#f1f5f9', fontSize: '1.125rem' }}>
                Ações Rápidas
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  component={RouterLink}
                  to="/"
                  variant="outlined"
                  size="large"
                  startIcon={<HomeIcon />}
                  fullWidth
                  sx={{ 
                    justifyContent: 'flex-start',
                    borderColor: 'rgba(102, 126, 234, 0.2)',
                    color: '#e2e8f0',
                    borderRadius: '12px',
                    py: 1.5,
                    '&:hover': {
                      borderColor: 'rgba(102, 126, 234, 0.4)',
                      background: 'rgba(102, 126, 234, 0.1)',
                    },
                  }}
                >
                  Voltar ao Início
                </Button>
                <Button
                  component={RouterLink}
                  to={`/modulos/${moduleId}/quiz`}
                  variant="contained"
                  size="large"
                  startIcon={<QuizIcon />}
                  fullWidth
                  sx={{ 
                    justifyContent: 'flex-start',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '12px',
                    py: 1.5,
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                    },
                  }}
                >
                  Fazer Quiz
                </Button>
              </Box>

              {module && (
                <Box sx={{ mt: 4, p: 3, background: 'linear-gradient(135deg, #667eea10 0%, #764ba210 100%)', borderRadius: '12px', border: '1px solid #667eea20' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#f1f5f9', mb: 1 }}>
                    {module.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
                    {bookmarked ? 'Módulo favoritado' : 'Continue estudando para dominar este conteúdo'}
                  </Typography>
                </Box>
              )}
            </NavigationSidebar>

            {/* Study Notes */}
            <NavigationSidebar elevation={0}>
              <StudyNotes moduleId={moduleId} onHighlightText={(text, color) => {
                // Add highlight functionality to content
                if (contentRef.current && studyMode === 'reading') {
                  const content = contentRef.current.innerHTML;
                  const highlightedContent = content.replace(
                    new RegExp(text, 'g'),
                    `<span class="highlighted" style="background-color: ${color}">${text}</span>`
                  );
                  contentRef.current.innerHTML = highlightedContent;
                }
              }} />
            </NavigationSidebar>
          </Box>
        </ContentContainer>
        </>
      )}

      {/* Floating Action Button to scroll to top */}
      <FloatingActionButton onClick={scrollToTop} size="medium">
        <KeyboardArrowUpIcon />
      </FloatingActionButton>

      {/* Achievement Notification Snackbar */}
      <Snackbar
        open={achievementSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setAchievementSnackbar({ ...achievementSnackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAchievementSnackbar({ ...achievementSnackbar, open: false })}
          severity="success"
          icon={<EmojiEventsIcon />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white',
            },
          }}
        >
          {achievementSnackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}